import { z } from 'zod';
import {
  ContradictionResolutionKindSchema,
  DeferredItemSchema,
  FINISH_NOTES_MAX_CHARS,
  clippedText,
} from './entities.js';
import {
  LitigationReviewV2DraftSchema,
  NullitiesAdmissibilityLegacyWriteContentSchema,
} from './wiki-document.js';

/**
 * Case Wiki — payloads des tools du Reviewer agent (PR2 — DEBT-029 / TICKET-124).
 *
 * Les agents post-lint sont séparés en deux familles :
 * - Consolidation : 3 passes write-capable qui nettoient le modèle du wiki.
 * - Review : 3 passes analysis-only qui produisent les documents avocat.
 *
 * Chaque passe reste un appel `AgenticSession.run` autonome avec son propre
 * prompt et un sous-ensemble de tools.
 *
 * Spec : `docs/architecture/case-wiki/data-model.md` §4.
 */

// ---------------------------------------------------------------------------
// upsert_wiki_document
// ---------------------------------------------------------------------------

/**
 * Types que la pipeline Reviewer active a le droit de produire. Les autres
 * valeurs de `WikiDocumentTypeSchema` restent lisibles pour compatibilité
 * avec les dossiers historiques, mais ne doivent plus être écrites par
 * `upsert_wiki_document`.
 */
export const ActiveReviewWikiDocumentTypeSchema = z.enum([
  'snapshot',
  'litigation_review',
  'nullities_admissibility',
]);
export type ActiveReviewWikiDocumentType = z.infer<
  typeof ActiveReviewWikiDocumentTypeSchema
>;

/**
 * Content stocké dans `case_wiki_documents.content` (jsonb).
 * Les documents narratifs actifs portent un markdown unique ;
 * `litigation_review` et `nullities_admissibility` portent une revue
 * structurée exploitable par l'UI.
 */
export const WikiDocumentMarkdownContentSchema = z.object({
  markdown: z.string().min(20).max(50_000),
});
export type WikiDocumentMarkdownContent = z.infer<typeof WikiDocumentMarkdownContentSchema>;

/**
 * Payload du tool `upsert_wiki_document`. Le Reviewer choisit `type` selon la
 * passe en cours, puis fournit `content` au format adapté.
 *
 * Versioning : la repository alloue automatiquement `version = max(version)+1`
 * pour chaque (case_id, type). L'agent ne le contrôle pas.
 *
 * Validation cross-field : les documents structurés exigent leur contrat
 * dédié, et les autres types actifs exigent `{ markdown: '...' }`.
 */
export const UpsertWikiDocumentPayloadSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('snapshot'),
    content: WikiDocumentMarkdownContentSchema,
  }).strict(),
  z.object({
    type: z.literal('litigation_review'),
    // The reviewer submits a V2 draft; the backend owns item UUIDs and profile stamping.
    content: LitigationReviewV2DraftSchema,
  }).strict(),
  z.object({
    type: z.literal('nullities_admissibility'),
    content: NullitiesAdmissibilityLegacyWriteContentSchema,
  }).strict(),
]);
export type UpsertWikiDocumentPayload = z.infer<typeof UpsertWikiDocumentPayloadSchema>;

// ---------------------------------------------------------------------------
// resolve_contradiction
// ---------------------------------------------------------------------------

/**
 * Payload du tool `resolve_contradiction`. La consolidation ou un chemin legacy
 * marque une contradiction existante comme analysée et choisit une issue
 * ternaire (M068) :
 *  - `resolved`  : tranchée (preuve écartée ou explicitement choisie)
 *  - `dismissed` : écartée comme sans incidence stratégique
 *  - `noted`     : conservée pour exploitation en plaidoirie
 *
 * `id` : UUID de la row `case_contradictions`. Les passes post-lint le
 * récupèrent via `list_contradictions_for_review` (read tool dédié — PR2.1).
 *
 * `resolved_note` correspond directement à la colonne SQL `resolved_note` —
 * la consolidation ou un chemin legacy y inscrit son raisonnement (10-1000 chars).
 */
export const ResolveContradictionPayloadSchema = z.object({
  id: z.string().uuid(),
  resolution_kind: ContradictionResolutionKindSchema,
  resolved_note: z.string().min(10).max(1000),
});
export type ResolveContradictionPayload = z.infer<typeof ResolveContradictionPayloadSchema>;

// ---------------------------------------------------------------------------
// finish_review_pass
// ---------------------------------------------------------------------------

/**
 * Payload terminal d'une passe Reviewer. La session boucle jusqu'à ce que
 * l'agent appelle ce tool (cf. `hasToolCall('finish_review_pass')` côté
 * AgenticSession). Persisté dans `case_wiki_runs.payload.finish` avec
 * `case_wiki_runs.payload.pass_index` ajouté par CaseReviewService.
 */
export const FinishReviewPassPayloadSchema = z.object({
  summary: z.string().min(20).max(12000),
  /** Compte d'écritures faites par cette passe (auto-déclaré par l'agent). */
  facts_updated: z.number().int().nonnegative().default(0),
  parties_updated: z.number().int().nonnegative().default(0),
  contradictions_resolved: z.number().int().nonnegative().default(0),
  wiki_documents_written: z.number().int().nonnegative().default(0),
  procedures_touched: z.number().int().nonnegative().optional(),
  facts_attached_to_procedure: z.number().int().nonnegative().optional(),
  /** Notes / questions ouvertes que la passe veut transmettre à la suivante. */
  notes: clippedText(0, FINISH_NOTES_MAX_CHARS).optional(),
  /** Items différés par cette passe — carry-forward vers la passe suivante ou l'humain. */
  deferred_items: z.array(DeferredItemSchema).max(200).default([]),
});
export type FinishReviewPassPayload = z.infer<typeof FinishReviewPassPayloadSchema>;

// ---------------------------------------------------------------------------
// Catalogue des passes post-lint
// ---------------------------------------------------------------------------

/**
 * Identité legacy d'une passe reviewer. Le schéma reste large pour parser les
 * anciens payloads Redis et l'historique `case_wiki_runs.payload.pass_name`.
 * Les passes actives sont uniquement celles listées dans `REVIEW_PASS_ORDER`.
 *
 * `verify_pivot_scores` et `assign_procedural_roles` migrent vers la phase
 * consolidation sous les noms `clean_facts` / `clean_participants`.
 * `generate_arguments` est legacy-only : les documents/rows historiques
 * restent lisibles, mais le pipeline actif ne l'exécute plus.
 */
export const ReviewPassNameSchema = z.enum([
  // Legacy — le champ `pivot_score` a été retiré du schéma en T6 (#462), mais
  // cette valeur d'enum reste nécessaire pour parser les `case_wiki_runs`
  // historiques dont `payload.pass_name === 'verify_pivot_scores'`.
  'verify_pivot_scores',
  'assign_procedural_roles',
  'generate_snapshot',
  'generate_arguments',
  'generate_litigation_review',
  'generate_nullities_admissibility',
]);
export type ReviewPassName = z.infer<typeof ReviewPassNameSchema>;

/** Phase d'exécution post-lint : consolidation structurelle, puis revue narrative. */
export const ReviewExecutionPhaseSchema = z.enum(['consolidation', 'review']);
export type ReviewExecutionPhase = z.infer<typeof ReviewExecutionPhaseSchema>;

/** Passes de consolidation actives entre lint et review. */
export const ConsolidationPassNameSchema = z.enum([
  'clean_facts',
  'clean_participants',
  'clean_procedure',
]);
export type ConsolidationPassName = z.infer<typeof ConsolidationPassNameSchema>;

export const CONSOLIDATION_PASS_ORDER: ConsolidationPassName[] = [
  'clean_facts',
  'clean_participants',
  'clean_procedure',
];

/**
 * Legacy queued Review payloads may still name retired passes while Redis drains.
 * Active execution order is `REVIEW_PASS_ORDER`; processors can parse these
 * payloads first, then filter to active passes before launching work.
 */
export const LEGACY_REVIEW_PASS_NAME_ACCEPTED_FOR_PAYLOAD_SCHEMA = ReviewPassNameSchema;
export type LegacyReviewPassNameAcceptedForPayload = z.infer<
  typeof LEGACY_REVIEW_PASS_NAME_ACCEPTED_FOR_PAYLOAD_SCHEMA
>;

/** Ordre canonique d'exécution des passes (source de vérité côté service). */
export const REVIEW_PASS_ORDER: ReviewPassName[] = [
  'generate_snapshot',
  'generate_litigation_review',
  'generate_nullities_admissibility',
];
